import { Box, HStack } from '@chakra-ui/react';
import { memo } from 'react';

interface ProgressDotsProps {
  /** Total number of steps in the active path. */
  count: number;
  /** 0-based index of the current step. */
  current: number;
  /** Accessible label describing progress, e.g. "Step 2 of 3". */
  label?: string;
}

/**
 * Minimal, unlabeled step indicator: a row of pills that fill up to the current
 * step. Three visual states so the final step never reads as "done": past steps
 * are a muted fill, the current step is a wider strong fill ("you are here"),
 * upcoming steps are an empty hairline. Renders nothing for a single-step path.
 *
 * Borders + background contrast only (no shadow). The pills are decorative
 * (`aria-hidden`); progress is announced via the group's `aria-label`.
 */
const ProgressDots = ({ count, current, label }: ProgressDotsProps) => {
  if (count <= 1) return null;

  return (
    <HStack gap="1" flex="none" role="group" aria-label={label}>
      {Array.from({ length: count }, (_, index) => {
        const isCurrent = index === current;
        const isPast = index < current;
        return (
          <Box
            key={index}
            aria-hidden
            h="5px"
            w={isCurrent ? '22px' : '16px'}
            rounded="full"
            bg={isCurrent ? 'primary.900' : isPast ? 'primary.400' : 'border.emphasized'}
            transition="background 0.15s, width 0.15s"
          />
        );
      })}
    </HStack>
  );
};

export default memo(ProgressDots);
